import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  Timer,
  Clock,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Alert, AlertDescription } from "@shared/ui/alert";
import {
  useSlaRules,
  useCreateSlaRule,
  useUpdateSlaRule,
  useToggleSlaRuleStatus,
} from "../../../../hooks";
import { SlaRule } from "../../../../types";

// ====================== TYPES ======================

interface SlaFormData {
  name: string;
  description?: string;
}

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== COMPONENTS ======================

// SLA Rule card component for better reusability
const SlaRuleCard: React.FC<{
  rule: SlaRule;
  onEdit: (rule: SlaRule) => void;
  onToggleActive: (rule: SlaRule) => void;
  isUpdating: boolean;
  currentSlaRule: SlaRule | null;
}> = ({ rule, onEdit, onToggleActive, isUpdating, currentSlaRule }) => {
  // Since responseTime might not be available from API, we'll use a default priority
  const getPriorityLevel = (responseTime?: number) => {
    if (!responseTime)
      return { level: "Standard", color: "text-blue-600 bg-blue-50" };
    if (responseTime <= 1)
      return { level: "Emergency", color: "text-red-600 bg-red-50" };
    if (responseTime <= 4)
      return { level: "High", color: "text-orange-600 bg-orange-50" };
    if (responseTime <= 12)
      return { level: "Medium", color: "text-yellow-600 bg-yellow-50" };
    return { level: "Standard", color: "text-green-600 bg-green-50" };
  };

  const priority = getPriorityLevel(rule.responseTime);

  return (
    <Card className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group">
      <CardContent className="p-0">
        <div className="p-4 border-b bg-muted/20">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-2">
              <Timer className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium">{rule.name}</h4>
                <p className="text-gray-700 mt-1 text-xs leading-relaxed">
                  {rule.description}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(rule)}
              className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
              disabled={isUpdating}
            >
              {isUpdating && currentSlaRule?.id === rule.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="p-4">
          <div className=" ">
            <div className="flex gap-2 items-center text-sm text-muted-foreground mb-2">
              <User className="h-3.5 w-3.5" />
              <p>{rule.createdBy}</p>
            </div>
            <div className="flex gap-2 items-center text-sm text-muted-foreground mb-4">
              <Calendar className="h-3.5 w-3.5" />
              <p>{rule.createdDate}</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <span className="text-sm">
              {rule.isActive ? (
                <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                  <Check className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                  <X className="h-3 w-3" /> Inactive
                </span>
              )}
            </span>
            <Switch
              checked={rule.isActive}
              onCheckedChange={() => onToggleActive(rule)}
              disabled={isUpdating}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
}> = ({ hasSearchQuery, onAddNew }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <Search className="h-10 w-10 text-muted-foreground/50" />
        <h4>No SLA rules found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add a new SLA rule"
            : "No SLA rules available"}
        </p>
        <Button variant="outline" className="mt-2" onClick={onAddNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New SLA Rule
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const SlaRuleTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentSlaRule, setCurrentSlaRule] = useState<SlaRule | null>(null);

  // Add state to track if we're in cancel mode
  const [isCanceling, setIsCanceling] = useState(false);

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: slaRules = [] } = useSlaRules(searchQuery);
  const createSlaRuleMutation = useCreateSlaRule();
  const updateSlaRuleMutation = useUpdateSlaRule();
  const toggleStatusMutation = useToggleSlaRuleStatus();

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "text",
        label: "SLA Name",
        placeholder: "Enter SLA name...",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear, descriptive name for the SLA rule",
      },
      {
        name: "description",
        type: "textarea",
        label: "Description",
        placeholder: "Enter description...",
        rows: 3,
        maxLength: 500,
        helperText: "Describe the SLA rule and its conditions",
        className: "pt-2",
      },
    ],
    []
  );

  // Create form instance using FormService - following established pattern
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      description: "",
    },
  });

  // Update form when editing an SLA rule - but not during cancel
  useEffect(() => {
    if (isCanceling) return; // Skip form updates during cancel

    if (currentSlaRule && isEditModalOpen) {
      const populateData = {
        name: currentSlaRule.name,
        description: currentSlaRule.description || "",
      };
      FormService.populateForm(form, populateData, formFields);
    } else if (isAddModalOpen) {
      const defaultValues = {
        name: "",
        description: "",
      };
      FormService.populateForm(form, defaultValues, formFields);
    }
  }, [
    currentSlaRule,
    isEditModalOpen,
    isAddModalOpen,
    form,
    formFields,
    isCanceling,
  ]);

  // Handle field changes - protected during cancel
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Prevent any field changes during cancel
      if (isCanceling) return;
    },
    [isCanceling]
  );

  // Handle form submission
  const handleSubmit = async (data: SlaFormData) => {
    if (isCanceling) return; // Prevent submit during cancel

    try {
      if (currentSlaRule) {
        const updatePayload = {
          id: currentSlaRule.id,
          name: data.name.trim(),
          description: data.description?.trim() || "",
        };
        // Update existing SLA rule
        await updateSlaRuleMutation.mutateAsync(updatePayload);
        setIsEditModalOpen(false);
      } else {
        const createPayload = {
          name: data.name.trim(),
          description: data.description?.trim() || "",
        };
        // Create new SLA rule
        await createSlaRuleMutation.mutateAsync(createPayload);
        setIsAddModalOpen(false);
      }

      // Reset form state - following established pattern
      FormService.resetForm(form, formFields);
      setCurrentSlaRule(null);
    } catch (error) {
      // Handle error
    }
  };

  const handleReset = useCallback(() => {
    if (isCanceling) return; // Prevent reset during cancel
    FormService.resetForm(form, formFields);
  }, [form, formFields, isCanceling]);

  // Event handlers
  const handleEditClick = useCallback((rule: SlaRule) => {
    setCurrentSlaRule(rule);
    setIsEditModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(
    async (rule: SlaRule) => {
      try {
        await toggleStatusMutation.mutateAsync({
          id: rule.id,
          isActive: !rule.isActive,
          name: rule.name,
        });
      } catch (error) {
        // Handle error
      }
    },
    [toggleStatusMutation]
  );

  // Handle modal close with cancel protection
  const handleAddModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsAddModalOpen(false);
    setCurrentSlaRule(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsEditModalOpen(false);
    setCurrentSlaRule(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    []
  );

  return (
    <div className="">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background pt-2 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start flex-col">
          <h3 className="page-subheading">SLA Rules</h3>
          <p className="page-subtitle">Create and manage SLA rules.</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createSlaRuleMutation.isPending}
        >
          {createSlaRuleMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New SLA Rule
        </Button>
      </div>

      {/* Grid */}
      <div className="relative pb-4">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search SLA rules..."
              className="pl-8 w-full sm:w-[250px]"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(slaRules) &&
          slaRules.map((rule) => (
            <SlaRuleCard
              key={rule.id}
              rule={rule}
              onEdit={handleEditClick}
              onToggleActive={handleToggleActive}
              isUpdating={
                updateSlaRuleMutation.isPending ||
                toggleStatusMutation.isPending
              }
              currentSlaRule={currentSlaRule}
            />
          ))}

        {Array.isArray(slaRules) && slaRules.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
          />
        )}
      </div>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New SLA Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createSlaRuleMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createSlaRuleMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={createSlaRuleMutation.isPending}
                  >
                    {createSlaRuleMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Save SLA Rule"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit SLA Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updateSlaRuleMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updateSlaRuleMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={updateSlaRuleMutation.isPending}
                  >
                    {updateSlaRuleMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update SLA Rule"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SlaRuleTab;
